// Required to satisfy TS typings for MUI lab theming.
// https://mui.com/material-ui/about-the-lab/#typescript
import type {} from '@mui/lab/themeAugmentation';
import Box from '@mui/material/Box';
import { buttonGroupClasses } from '@mui/material/ButtonGroup';
import { tabClasses } from '@mui/material/Tab';
import { tabScrollButtonClasses } from '@mui/material/TabScrollButton';
import { createTheme, Theme } from '@mui/material/styles';
import type {} from '@mui/x-data-grid/themeAugmentation';
import type {} from '@mui/x-data-grid-premium/themeAugmentation';

import {
  CODE_FONT_FAMILY,
  SANS_SERIF_FONT_FAMILY,
  SERIF_FONT_FAMILY,
} from '@watershed/fonts/families';
import ChevronDownIcon from '@watershed/icons/components/ChevronDown';
import ChevronRightIcon from '@watershed/icons/components/ChevronRight';
import ChevronLeftIcon from '@watershed/icons/components/ChevronLeft';
import CloseIcon from '@watershed/icons/components/Close';
import { SupportedLocale } from '@watershed/intl/constants';
// eslint-disable-next-line no-restricted-imports
import { palette } from '@watershed/style/palette';
import {
  getPaletteUtils,
  iconSizeMedium,
  menuItemPadding,
  popoverTransitionDuration,
  SPACING_PX,
} from '@watershed/style/styleUtils';
import CheckmarkIcon from '@watershed/ui-core/components/CheckmarkIcon';

import { isButtonIconOnly } from './utils';

const paletteUtils = getPaletteUtils(palette);

import {
  backdropClasses,
  buttonClasses,
  iconButtonClasses,
  selectClasses,
} from '@mui/material';
import {
  deDE as coreDeDE,
  enUS as coreEnUS,
  esES as coreEsES,
  frFR as coreFrFR,
  idID as coreIdID,
  itIT as coreItIT,
  jaJP as coreJaJP,
  plPL as corePlPL,
  ptBR as corePtBR,
  trTR as coreTrTR,
  zhCN as coreZhCN,
} from '@mui/material/locale';
import {
  deDE as xDataGridDeDE,
  enUS as xDataGridEnUS,
  esES as xDataGridEsES,
  frFR as xDataGridFrFR,
  itIT as xDataGridItIT,
  jaJP as xDataGridJaJP,
  plPL as xDataGridPlPL,
  ptBR as xDataGridPtBR,
  trTR as xDataGridTrTR,
  zhCN as xDataGridZhCN,
} from '@mui/x-data-grid/locales';
// eslint-disable-next-line no-restricted-imports
import {
  deDE as xDatePickersDeDE,
  enUS as xDatePickersEnUS,
  esES as xDatePickersEsES,
  frFR as xDatePickersFrFR,
  itIT as xDatePickersItIT,
  jaJP as xDatePickersJaJP,
  plPL as xDatePickersPlPL,
  ptBR as xDatePickersPtBR,
  trTR as xDatePickersTrTR,
  zhCN as xDatePickersZhCN,
} from '@mui/x-date-pickers-pro';

// Note: line heights are relative based on font size, so if you change font
// size change line height too!
const typography = {
  // This is a special display font that's large and uses our serif font (Makinac).
  title: {
    fontFamily: SERIF_FONT_FAMILY,
    color: palette.midnight,
    fontSize: 40,
    lineHeight: '48px',
    fontWeight: 500,
    letterSpacing: '-0.004em',
    textWrapStyle: 'pretty',
  },
  h1: {
    color: palette.text.primary,
    fontSize: 24,
    fontWeight: 700,
    lineHeight: 32 / 24,
    fontVariantNumeric: 'tabular-nums',
    textWrapStyle: 'pretty',
  },
  h2: {
    color: palette.text.primary,
    fontSize: 18,
    fontWeight: 600,
    lineHeight: 24 / 18,
    fontVariantNumeric: 'tabular-nums',
    textWrapStyle: 'pretty',
  },
  h3: {
    fontSize: 16,
    fontWeight: 600,
    lineHeight: 24 / 16,
    fontVariantNumeric: 'tabular-nums',
    textWrapStyle: 'pretty',
  },
  h4: {
    color: palette.text.primary,
    fontSize: 14, // same as body1, but bolder.
    fontWeight: 600,
    lineHeight: 24 / 14,
    fontVariantNumeric: 'tabular-nums',
    textWrapStyle: 'pretty',
  },
  // Same as h4 but with secondary color
  h5: {
    color: palette.text.secondary,
    fontSize: 14, // same as body1, but bolder.
    fontWeight: 600,
    lineHeight: 24 / 14,
    fontVariantNumeric: 'tabular-nums',
    textWrapStyle: 'pretty',
  },
  body1: {
    color: palette.text.primary,
    fontSize: 14,
    fontWeight: 400,
    lineHeight: 24 / 14,
    fontVariantNumeric: 'tabular-nums',
    textWrapStyle: 'pretty',
    '& > a': {
      color: palette.primary.main,
    },
  },
  body2: {
    color: palette.text.secondary,
    fontSize: 14,
    fontWeight: 400,
    lineHeight: 24 / 14,
    fontVariantNumeric: 'tabular-nums',
    textWrapStyle: 'pretty',
    '& > a': {
      color: palette.primary.main,
    },
  },
  body3: {
    fontSize: 13,
    fontWeight: 400,
    lineHeight: 20 / 13,
    fontVariantNumeric: 'tabular-nums',
    textWrapStyle: 'pretty',
  },
  caption: {
    color: palette.text.secondary,
    fontSize: 13,
    fontWeight: 600,
    lineHeight: 20 / 13,
    fontVariantNumeric: 'tabular-nums',
    textWrapStyle: 'pretty',
  },
} as const;

export function getTheme(locale: SupportedLocale): Theme {
  // TODO: Can we use dynamic imports instead?
  const muiLocaleConfigs = {
    'en-US': [xDatePickersEnUS, xDataGridEnUS, coreEnUS],
    'en-GB': [xDatePickersEnUS, xDataGridEnUS, coreEnUS],
    'en-PL': [xDatePickersJaJP, xDataGridJaJP, coreJaJP],
    'ja-JP': [xDatePickersJaJP, xDataGridJaJP, coreJaJP],
    'de-DE': [xDatePickersDeDE, xDataGridDeDE, coreDeDE],
    'fr-FR': [xDatePickersFrFR, xDataGridFrFR, coreFrFR],
    'pt-BR': [xDatePickersPtBR, xDataGridPtBR, corePtBR],
    'es-ES': [xDatePickersEsES, xDataGridEsES, coreEsES],
    'pl-PL': [xDatePickersPlPL, xDataGridPlPL, corePlPL],
    'tr-TR': [xDatePickersTrTR, xDataGridTrTR, coreTrTR],
    'zh-CN': [xDatePickersZhCN, xDataGridZhCN, coreZhCN],
    'it-IT': [xDatePickersItIT, xDataGridItIT, coreItIT],
    'id-ID': [xDatePickersEnUS, xDataGridEnUS, coreIdID], // DatePicker and DataGrid do not support Indonesian yet
  }[locale] ?? [xDatePickersEnUS, xDataGridEnUS, coreEnUS];

  return createTheme(
    {
      components: {
        MuiAvatar: {
          styleOverrides: {
            root: {
              fontWeight: 600,
            },
            rounded: {
              borderRadius: '4px',
            },
          },
        },
        MuiAvatarGroup: {
          styleOverrides: {
            // By default, the avatars are right-aligned with `flexDirection:
            // 'row-reverse'`, so override this and also adjust the margins to match
            // the left-alignment.
            root: {
              flexDirection: 'row',
              '& .MuiAvatar-root:first-of-type': {
                marginLeft: 0,
              },
              '& .MuiAvatar-root:last-of-type': {
                marginLeft: '-8px',
              },
            },
          },
        },
        MuiPaper: {
          styleOverrides: {
            elevation1: {
              boxShadow: paletteUtils.boxShadow,
            },
            elevation8: {
              boxShadow: paletteUtils.boxShadowMenu,
            },
            elevation9: {
              boxShadow: paletteUtils.boxShadowLg,
            },
            rounded: {
              borderRadius: '10px',
            },
          },
        },
        MuiDialog: {
          styleOverrides: {
            paper: {
              boxShadow: paletteUtils.boxShadowLg,
            },
            container: {
              // secondary.dark at 60%
              backgroundColor: `${palette.grey100}60`,
            },
          },
        },
        MuiBackdrop: {
          styleOverrides: {
            root: {
              backgroundColor: 'transparent',
              // This is a hack to prevent the backdrop from being clickable when the modal is hidden :(
              // see: https://github.com/mui/material-ui/issues/32286
              '&[style*="opacity: 0"]': {
                pointerEvents: 'none',
              },
            },
          },
        },
        // This is a hack to prevent the backdrop from being clickable when the modal is hidden :(
        // see: https://github.com/mui/material-ui/issues/32286
        MuiModal: {
          styleOverrides: {
            root: {
              [`&:has(> div.${backdropClasses.root}[style*="opacity: 0"])`]: {
                pointerEvents: 'none',
              },
            },
          },
        },
        MuiDialogTitle: {
          styleOverrides: {
            root: {
              borderBottom: `1px solid ${palette.divider}`,
              padding: 24,
              '& h2': {
                ...typography.h1,
                overflowWrap: 'break-word',
              },
            },
          },
        },
        MuiDialogContent: {
          styleOverrides: {
            root: {
              padding: '16px',
            },
          },
        },
        MuiDialogActions: {
          defaultProps: {
            disableSpacing: true, // use gap instead
          },
          styleOverrides: {
            root: {
              borderTop: `1px solid ${palette.divider}`,
              padding: '12px',
              gap: '8px',
            },
          },
        },
        MuiTypography: {
          styleOverrides: {
            ...typography,
          },
        },
        MuiContainer: {
          styleOverrides: {
            maxWidthMd: {
              maxWidth: '1024px !important', // 900px is default, in a media query
            },
          },
        },
        MuiSlider: {
          styleOverrides: {
            root: {
              padding: '10px 0',
            },
            thumb: {
              backgroundColor: palette.white,
              boxShadow: paletteUtils.boxShadow,
            },
            rail: {
              backgroundColor: palette.grey20,
              opacity: 1.0,
            },
            mark: {
              backgroundColor: palette.grey30,
              height: '4px',
              width: '4px',
              borderRadius: '50%',
              top: '50%',
              transform: 'translate(-50%, -50%)',
            },
          },
        },
        MuiButtonBase: {
          defaultProps: {
            disableRipple: true,
          },
          styleOverrides: {
            root: {
              letterSpacing: '-0.03em',
            },
          },
        },
        MuiButtonGroup: {
          defaultProps: {
            variant: 'contained',
            color: 'secondary',
            disableRipple: true,
          },
          styleOverrides: {
            contained: {
              color: palette.text.primary,
              backgroundColor: palette.white,
              boxShadow: paletteUtils.boxShadowButton.secondary.default,
            },
            grouped: {
              // CSS specificity hack to override all variants & pseudo-states without !important
              [`&&.${buttonGroupClasses.disabled}`]: {
                color: palette.text.disabled,
                backgroundImage: 'none',
                backgroundColor: palette.background.paper,
                boxShadow: paletteUtils.boxShadowButtonGrouped.disabled,
                border: 'none',
              },
              marginLeft: '-1px',
              [`& :not(${buttonGroupClasses.lastButton})`]: {
                borderRight: 0,
              },
              '&:focus': {
                zIndex: 1,
              },
            },
          },
          variants: [
            {
              props: { variant: 'contained', color: 'secondary' },
              style: {
                [`& .${buttonGroupClasses.grouped}`]: {
                  boxShadow:
                    paletteUtils.boxShadowButtonGrouped.secondary.default,
                  '&:hover,  &:active': {
                    boxShadow:
                      paletteUtils.boxShadowButtonGrouped.secondary.hover,
                  },
                  '&:focus-visible': {
                    boxShadow: paletteUtils.boxShadowButtonGrouped.focus,
                  },
                },
              },
            },
          ],
        },
        // TODO: @dgattey fix this all to use variants instead of styleOverrides
        // where possible so we can remove the specifics
        MuiButton: {
          defaultProps: {
            variant: 'contained',
            color: 'secondary',
          },
          styleOverrides: {
            root: ({ ownerState }) => ({
              variants: [
                {
                  // Text and secondary together
                  props: { variant: 'text', color: 'secondary' },
                  style: {
                    color: palette.text.primary,
                    '&:hover, &:focus': {
                      backgroundColor: palette.grey10,
                    },
                    '&:active': {
                      backgroundColor: palette.grey20,
                    },
                    '&:focus-visible': {
                      boxShadow: paletteUtils.boxShadowButton.focus,
                    },
                    // CSS specificity hack to override all variants & pseudo-states without !important
                    '&.Mui-disabled.Mui-disabled.Mui-disabled': {
                      color: palette.text.disabled,
                    },
                  },
                },
                {
                  // Text and primary together
                  props: { variant: 'text', color: 'primary' },
                  style: {
                    color: palette.primary.main,
                    '&:hover, &:focus': {
                      backgroundColor: palette.primary.light,
                      color: palette.primary.dark,
                    },
                    '&:focus-visible': {
                      boxShadow: paletteUtils.boxShadowButton.focus,
                    },
                    // CSS specificity hack to override all variants & pseudo-states without !important
                    '&.Mui-disabled.Mui-disabled.Mui-disabled': {
                      color: palette.cobalt30,
                    },
                  },
                },
                {
                  // Contained and primary together
                  props: { variant: 'contained', color: 'primary' },
                  style: {
                    color: palette.white,
                    boxShadow: paletteUtils.boxShadowButton.primary.default,
                    backgroundColor: palette.primary.main,
                    '&:hover': {
                      backgroundColor: palette.cobalt70,
                      boxShadow: paletteUtils.boxShadowButton.primary.default,
                    },
                    '&:active': {
                      backgroundColor: palette.cobalt100,
                    },
                    '&:focus-visible': {
                      boxShadow: paletteUtils.boxShadowButton.focus,
                    },
                    // CSS specificity hack to override all variants & pseudo-states without !important
                    '&.Mui-disabled.Mui-disabled.Mui-disabled': {
                      backgroundColor: palette.cobalt30,
                      color: palette.white,
                      boxShadow: 'none',
                    },
                  },
                },
                {
                  // Contained and secondary together
                  props: { variant: 'contained', color: 'secondary' },
                  style: {
                    color: palette.text.primary,
                    backgroundColor: palette.white,
                    boxShadow: paletteUtils.boxShadowButton.secondary.default,
                    '&:hover': {
                      color: palette.text.primary,
                      backgroundColor: palette.grey10,
                      boxShadow: paletteUtils.boxShadowButton.secondary.default,
                    },
                    '&:active': {
                      backgroundColor: palette.grey20,
                    },
                    '&:focus-visible': {
                      boxShadow: paletteUtils.boxShadowButton.focus,
                    },
                    '&.Mui-disabled.Mui-disabled.Mui-disabled': {
                      color: palette.text.disabled,
                    },
                  },
                },
                {
                  // Contained and error together
                  props: { variant: 'contained', color: 'error' },
                  style: {
                    color: palette.sun,
                    backgroundColor: palette.white,
                    boxShadow: paletteUtils.boxShadowButton.destructive.default,
                    '&:hover': {
                      color: palette.white,
                      backgroundColor: palette.sun50,
                      boxShadow:
                        paletteUtils.boxShadowButton.destructive.default,
                    },
                    '&:active': {
                      backgroundColor: palette.error.dark,
                    },
                    '&:focus-visible': {
                      boxShadow: paletteUtils.boxShadowButton.destructive.focus,
                    },
                    '&.Mui-disabled.Mui-disabled.Mui-disabled': {
                      color: palette.sun30,
                    },
                  },
                },
                {
                  // Contained and small together
                  props: { variant: 'contained', size: 'small' },
                  style: {
                    lineHeight: '20px',
                    height: 24,
                    fontSize: 14,
                  },
                },
              ],
              letterSpacing: '-0.03em',
              textTransform: 'none',
              fontSize: 14,
              lineHeight: '16px',
              fontWeight: 600,

              flexShrink: 0,
              height: 32,
              padding: '0 12px',
              borderRadius: 6,
              textDecoration: 'none !important',
              transition: paletteUtils.transition,
              transitionProperty:
                'color, background-color, background-image, box-shadow',
              ...(isButtonIconOnly(ownerState) && {
                minWidth: ownerState.size === 'small' ? 24 : 32,
                padding: 0,
              }),

              // When the button is small and not icon only, icons should be 12px
              [`&.${buttonClasses.sizeSmall} > .${buttonClasses.icon}`]: {
                ...(!isButtonIconOnly(ownerState)
                  ? {
                      '& svg': {
                        width: 12,
                        height: 12,
                      },
                    }
                  : {}),
              },

              // When the button is medium and not icon only, icons should be 16px
              [`&.${buttonClasses.sizeMedium} > .${buttonClasses.icon}`]: {
                ...(!isButtonIconOnly(ownerState)
                  ? {
                      '& svg': {
                        width: iconSizeMedium,
                        height: iconSizeMedium,
                      },
                    }
                  : {}),
              },
            }),
            endIcon: ({ ownerState }) =>
              isButtonIconOnly(ownerState)
                ? { margin: 0 }
                : {
                    marginLeft: 4,
                  },
            startIcon: ({ ownerState }) =>
              isButtonIconOnly(ownerState)
                ? { margin: 0 }
                : {
                    marginRight: 4,
                  },
            sizeSmall: ({ ownerState }) =>
              isButtonIconOnly(ownerState)
                ? {}
                : {
                    height: 28,
                    minWidth: 32,
                    fontSize: typography.body1.fontSize,
                  },
            sizeLarge: {
              padding: '0 12px',
            },
            contained: {
              // CSS specificity hack to override all variants & pseudo-states without !important
              '&.Mui-disabled.Mui-disabled.Mui-disabled': {
                color: palette.text.disabled,
                backgroundImage: 'none',
                backgroundColor: palette.background.paper,
                boxShadow: paletteUtils.boxShadowButton.disabled,
                opacity: 0.5,
              },
            },
            text: ({ ownerState }) =>
              isButtonIconOnly(ownerState)
                ? {}
                : {
                    padding: '0 12px',
                  },
          },
        },
        MuiToggleButton: {
          styleOverrides: {
            root: {
              font: 'inherit',
              textTransform: 'none',
              fontWeight: 600,
            },
            sizeMedium: {
              '& svg': {
                width: iconSizeMedium,
                height: iconSizeMedium,
              },
            },
          },
        },
        MuiIconButton: {
          styleOverrides: {
            root: {
              color: palette.text.primary,
              padding: 0,
              borderRadius: 6,
              flexShrink: 0,
              '&:focus-visible': {
                color: palette.primary.main,
                boxShadow: paletteUtils.boxShadowButton.focus,
              },
              '&:active': {
                backgroundColor: palette.secondary.light,
              },
              [`&.${iconButtonClasses.disabled}`]: {
                color: palette.text.disabled,
              },
            },
            sizeSmall: {
              padding: 0,
            },
            sizeLarge: {
              padding: 6,
            },
            colorPrimary: {
              boxShadow: paletteUtils.boxShadowButton.secondary.default,
              backgroundColor: palette.white,
              color: palette.text.primary,
              padding: 4,
              transition: paletteUtils.transition,
              transitionProperty: 'color, box-shadow',
              '&:hover': {
                color: palette.text.primary,
                backgroundColor: palette.background.default,
                boxShadow: paletteUtils.boxShadowButton.secondary.hover,
              },
              '&:focus-visible': {
                color: palette.text.primary,
                boxShadow: paletteUtils.boxShadowButton.focus,
              },
            },
            colorSecondary: {
              color: palette.secondary.dark,
              transition: paletteUtils.transition,
              transitionProperty: 'color',
              '&:hover': {
                color: palette.text.primary,
              },
              '&:focus-visible': {
                color: palette.primary.main,
              },
            },
          },
        },
        MuiPickersDay: {
          styleOverrides: {
            root: {
              fontWeight: 400,
              borderRadius: 6,
              border: '0 !important',
              '&:hover, &:focus': {
                backgroundColor: palette.grey10,
              },
              '&:focus-visible': {
                boxShadow: paletteUtils.boxShadowButton.focus,
              },
              '&.Mui-selected, &.Mui-selected:focus': {
                backgroundColor: palette.primary.main,
              },
            },
            today: {
              '&:not(.Mui-selected)': {
                backgroundColor: palette.primary.light,
              },
            },
          },
        },
        MuiDayPicker: {
          styleOverrides: {
            weekContainer: {
              margin: 0,
              padding: '2px 0',
            },
          },
        },
        MuiPickersCalendarHeader: {
          styleOverrides: {
            labelContainer: {
              overflow: 'visible',
            },
          },
        },
        MuiAutocomplete: {
          defaultProps: {
            clearIcon: <CloseIcon />,
            popupIcon: <ChevronDownIcon />,
            // Prevent icon from shrinking when chip text is long
            ChipProps: {
              deleteIcon: (
                <Box
                  sx={{
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <CloseIcon />
                </Box>
              ),
            },
          },
          styleOverrides: {
            root: {
              overflow: 'visible',
              position: 'relative',
              '&.Mui-focused .MuiAutocomplete-inputRoot': {
                boxShadow: paletteUtils.boxShadowField.focus,
              },
              '&.Mui-focused .MuiFilledInput-root': {
                boxShadow: 'none',
                background: palette.grey20,
              },
              '& .MuiFilledInput-root': {
                background: palette.grey10,
                paddingTop: '4px',
                paddingBottom: '4px',
                paddingLeft: '12px',

                '&:hover': {
                  background: palette.grey20,
                },

                '& svg': {
                  width: 16,
                  height: 16,
                },
              },
              '& .MuiOutlinedInput-notchedOutline': {
                display: 'none',
              },
            },
            inputRoot: {
              '&:not(.MuiFilledInput-root)': {
                backgroundColor: palette.white,
                boxShadow: paletteUtils.boxShadowField.base,
              },
              color: palette.text.primary,
              borderRadius: 6,
              '&:before, &:after': {
                // content: 'unset !important',
                display: 'none !important',
              },
            },
            input: {
              font: 'inherit',
              letterSpacing: 'inherit',
              appearance: 'none',
              padding: '0 !important',
              lineHeight: '24px',
              height: 'auto',
              minWidth: '5ch !important',
              '&.Mui-disabled': {
                cursor: 'not-allowed',
              },
            },
            popupIndicator: {
              color: 'inherit',
            },
            clearIndicator: {
              color: 'inherit',
            },
            tagSizeSmall: {
              margin: 0,
              marginRight: 4,
            },
            groupLabel: {
              paddingTop: '6px',
              paddingBottom: '6px',
              fontWeight: 'normal',
              background: palette.panel,
              color: palette.text.secondary,
              top: 0,
            },
            paper: {
              borderRadius: 6,
              backgroundColor: palette.background.paper,
              boxShadow: paletteUtils.boxShadowMenu,
            },
            listbox: {
              padding: 0,
            },
            option: {
              lineHeight: '20px',
              '&[aria-selected="true"]': {
                backgroundColor: palette.secondary.light,
              },
              '&[data-focus="true"]': {
                backgroundColor: palette.secondary.light,
              },
            },
            endAdornment: {
              // Defaults to `calc(50% - 14px)` but that is too high, not vertically centered
              top: 'calc(50% - 12px);',
              transform: 'none',
            },

            // This is getting overridden with margin 0, which has option tags touching each other,
            // which is gross.
            tag: {
              marginBottom: '2px !important',
              marginTop: '2px  !important',
              borderRadius: '4px', // default 6px doesn't nest well
            },
          },
        },
        MuiSelect: {
          defaultProps: {
            MenuProps: {
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left',
              },
              transformOrigin: {
                vertical: 'top',
                horizontal: 'left',
              },
            },
            IconComponent: ChevronDownIcon,
          },
          styleOverrides: {
            select: {
              font: 'inherit',
              letterSpacing: 'inherit',
              backgroundColor: palette.white,
              color: palette.text.primary,
              border: 0,
              boxShadow: paletteUtils.boxShadowField.base,
              borderRadius: 6,
              appearance: 'none',
              padding: '4px 28px 4px 12px !important',
              lineHeight: '24px',
              '&:focus': {
                outline: 0,
                boxShadow: paletteUtils.boxShadowField.focus,
              },
              '&:before, &:after': {
                // content: 'unset !important',
                display: 'none !important',
              },
              [`&.${selectClasses.disabled}`]: {
                color: palette.text.disabled,
                backgroundColor: palette.background.paper,
                cursor: 'not-allowed',
              },
            },
            icon: {
              color: 'inherit',
              top: 'auto !important',
              right: 8,
              width: 14,
              height: 14,
              [`&.${selectClasses.disabled}`]: {
                color: palette.text.disabled,
              },
            },
          },
        },
        MuiSwitch: {
          styleOverrides: {
            root: {
              width: 40,
              height: 24,
              padding: 0,
              overflow: 'hidden',
            },
            switchBase: {
              padding: 2,
              '&.Mui-checked': {
                transform: 'translateX(16px)',
              },
            },
            colorPrimary: {
              color: palette.white,
              '&:active': {
                backgroundColor: 'unset !important',
              },
              '&.Mui-checked': {
                color: palette.white,
                '& + .MuiSwitch-track': {
                  backgroundColor: palette.primary.main,
                  opacity: 1,
                },
                '&:checked': {
                  backgroundColor: palette.primary.main,
                },
                '&:hover': {
                  backgroundColor: 'transparent',
                },
                // Add disabled styles for checked state
                '&.Mui-disabled': {
                  '& + .MuiSwitch-track': {
                    backgroundColor: palette.cobalt30, // Use a muted color for disabled track
                    opacity: 0.5,
                  },
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                  '& .MuiSwitch-thumb': {
                    backgroundColor: palette.white, // Color for the thumb when disabled
                  },
                },
              },
              // Add disabled styles for unchecked state
              '&.Mui-disabled': {
                color: palette.grey30,
                '& + .MuiSwitch-track': {
                  backgroundColor: palette.grey20,
                  opacity: 0.5,
                },
                '& .MuiSwitch-thumb': {
                  backgroundColor: palette.grey40,
                },
              },
            },
            thumb: {
              boxShadow: paletteUtils.boxShadow,
            },
            track: {
              width: '100%',
              height: '100%',
              borderRadius: 12,
              opacity: 1,
              backgroundColor: palette.secondary.main,
              transition: paletteUtils.transition,
              transitionProperty: 'background-color',
            },
            sizeSmall: {
              width: 32,
              height: 20,
              padding: 0,
              '& .MuiSwitch-switchBase': {
                padding: 2,
                '&.Mui-checked': {
                  transform: 'translateX(12px)',
                },
              },
              '& .MuiSwitch-thumb': {
                width: 16,
                height: 16,
                boxShadow: 'none',
              },
            },
          },
        },
        MuiFormControlLabel: {
          styleOverrides: {
            root: {
              marginInline: 0,
              '& .MuiFormControlLabel-label.Mui-disabled': {
                color: palette.grey70,
              },
              '&.MuiFormControlLabel-labelPlacementEnd .MuiSwitch-root + .MuiFormControlLabel-label':
                {
                  marginLeft: 8,
                },
            },
          },
        },
        MuiCard: {
          defaultProps: {
            variant: 'outlined',
          },
          styleOverrides: {
            root: {
              borderRadius: '6px',
            },
          },
        },
        MuiTooltip: {
          defaultProps: {
            arrow: false,
          },
          styleOverrides: {
            tooltip: {
              backgroundColor: palette.white,
              boxShadow: paletteUtils.boxShadowMd,
              color: palette.text.primary,
              fontSize: 14,
              fontWeight: 400,
              padding: '8px 12px',
              textWrap: 'pretty',
              whiteSpace: 'pre-line',
            },
          },
        },
        MuiPopover: {
          defaultProps: {
            transitionDuration: popoverTransitionDuration,
          },
          styleOverrides: {
            paper: {
              borderRadius: '10px',
              boxShadow: paletteUtils.boxShadowMenu,
            },
          },
        },
        MuiMenu: {
          defaultProps: {
            transitionDuration: popoverTransitionDuration,
          },
          styleOverrides: {
            list: {
              paddingTop: 0,
              paddingBottom: 0,
            },
            paper: {
              backgroundColor: palette.background.paper,
              boxShadow: paletteUtils.boxShadowMenu,
              marginTop: 4,
            },
          },
        },
        MuiMenuItem: {
          styleOverrides: {
            root: {
              '&:first-of-type': {
                borderTop: `none`,
              },
              borderTop: `1px solid ${palette.border}`,
              fontSize: typography.body1.fontSize,
              ...menuItemPadding,
              lineHeight: '20px',
              '&:hover, &:active': {
                backgroundColor: palette.info.light,
              },
              '&.Mui-selected': {
                '&, &:hover': {
                  backgroundColor: palette.info.light,
                },
              },
              '& .MuiTypography-root': {
                lineHeight: 'inherit',
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              },
            },
          },
        },
        MuiChip: {
          defaultProps: {
            deleteIcon: <CloseIcon />,
          },
          styleOverrides: {
            root: ({ ownerState: { color = 'secondary', clickable } }) => ({
              // Specificity increase to override defaults
              '&&:hover, &&.Mui-focusVisible': clickable
                ? {
                    backgroundColor:
                      paletteUtils.chipColors[color].hoverBackground,
                    color: paletteUtils.chipColors[color].text,
                    fill: paletteUtils.chipColors[color].text,
                  }
                : undefined,
              backgroundColor: paletteUtils.chipColors[color].background,
              color: paletteUtils.chipColors[color].text,
              fill: paletteUtils.chipColors[color].text,
              borderRadius: '6px',
              fontSize: 14,
              fontWeight: 600,
              height: 24,
              lineHeight: '24px',
              letterSpacing: '-0.48px',
            }),
            label: ({ theme }) => ({
              paddingLeft: theme.spacing(1),
              paddingRight: theme.spacing(1),
            }),
            icon: ({ theme, ownerState }) => ({
              marginLeft: theme.spacing(1),
              marginRight: theme.spacing(-0.5),
              height: 16,
              width: 16,
              ...(ownerState.color === 'default' && {
                color: palette.info.main,
              }),
            }),
            outlined: ({ ownerState: { color = 'secondary' } }) => ({
              backgroundColor: palette.background.paper,
              border: '1px solid',
              borderColor: paletteUtils.chipColors[color].background,
              '&&:hover, &&.Mui-focusVisible': {
                backgroundColor:
                  paletteUtils.chipColors[color].outlinedHoverBackground,
              },
            }),
            deleteIcon: {
              flexShrink: 0,
              '&, &:hover': {
                color: palette.secondary.dark,
              },
            },
            sizeSmall: {
              height: 20,
              fontSize: 13,
            },
            clickable: {
              transition: paletteUtils.transition,
              transitionProperty: 'color, background-color, fill',
              '&:active': {
                boxShadow: 'none',
              },
            },
          },
        },
        MuiInputLabel: {
          defaultProps: {
            shrink: false,
          },
          styleOverrides: {
            root: {
              ...typography.body1,
            },
            formControl: {
              top: 'unset',
              left: 'unset',
              position: 'unset',
              transform: 'unset',
            },
          },
        },
        MuiFormHelperText: {
          styleOverrides: {
            root: {
              marginTop: 8,
            },
          },
        },
        MuiInput: {
          defaultProps: {
            disableUnderline: true,
          },
        },
        MuiInputBase: {
          styleOverrides: {
            input: {
              letterSpacing: '-0.03em',
            },
          },
        },
        MuiOutlinedInput: {
          styleOverrides: {
            root: {
              border: 'none',
              '& .MuiOutlinedInput-notchedOutline': {
                display: 'none',
              },

              '& svg': {
                width: 16,
                height: 16,
              },
            },
            colorSecondary: {
              ...paletteUtils.textFieldStyles,
              variants: [],
            },
            notchedOutline: {
              display: 'none',
            },
            adornedEnd: {
              '& > svg': {
                fill: palette.info.main,
              },
            },
          },
        },

        MuiListItemIcon: {
          styleOverrides: {
            root: {
              color: palette.text.secondary,
            },
          },
        },

        MuiListSubheader: {
          styleOverrides: {
            root: {
              ...typography.h4,
              paddingTop: 8,
              paddingBottom: 8,
            },
            gutters: {
              // To match `MuiMenuItem` override.
              ...menuItemPadding,
            },
          },
        },
        MuiTabs: {
          defaultProps: {
            variant: 'scrollable',
            scrollButtons: 'auto',
            slots: {
              startScrollButtonIcon: ChevronLeftIcon,
              endScrollButtonIcon: ChevronRightIcon,
            },
          },
          styleOverrides: {
            root: {
              minHeight: 'unset',
            },
            fixed: {
              overflow: 'visible !important',
            },
            list: {
              gap: 16,
            },
            indicator: {
              backgroundColor: palette.primary.main,
            },
          },
        },
        MuiTabScrollButton: {
          styleOverrides: {
            root: {
              transition: paletteUtils.transition,
              transitionProperty: 'opacity',
              // Why does MUI not use their own disabled variant here? …
              [`&.${tabScrollButtonClasses.disabled}`]: {
                opacity: 1,
                color: palette.text.disabled,
              },
              // MUI doesn't provide a helper class for left button…
              '&:first-child': {
                width: '28px', // IconButton width
                justifyContent: 'flex-start',
                marginLeft: '-5px', // Magic number to optically align chevron with content
              },
            },
          },
        },
        MuiTab: {
          styleOverrides: {
            root: {
              fontSize: 14,
              color: palette.text.secondary,
              letterSpacing: 'inherit',
              lineHeight: '16px',
              textTransform: 'none',
              minHeight: 'unset !important',
              minWidth: 'unset !important',
              padding: '12px 0',
              transition: paletteUtils.transition,
              transitionProperty: 'color, box-shadow',
              flexDirection: 'row',
              gap: 8,
              '&:hover': {
                color: palette.text.primary,
              },
              '&:focus': {
                outline: 0,
              },
              [`&.${tabClasses.disabled}`]: {
                color: palette.text.disabled,
              },
              '& svg': {
                margin: '0 !important',
                width: 16,
                height: 16,
              },
            },
            selected: {
              color: palette.text.primary,
            },
            labelIcon: {
              paddingTop: 6,
            },
            textColorInherit: {
              opacity: 1,
            },
          },
        },
        MuiTabPanel: {
          styleOverrides: {
            root: {
              padding: 0,
            },
          },
        },
        MuiLink: {
          styleOverrides: {
            root: {
              color: palette.primary.main, // otherwise typography variants override the color
              outlineColor: palette.primary.main,
            },
            button: ({ ownerState }) => ({
              letterSpacing: 'inherit',
              // allows variant font size to override our default
              fontSize:
                ownerState.variant == null || ownerState.variant === 'inherit'
                  ? 15
                  : undefined,
            }),
            underlineHover: {
              '& h1:hover, & h2:hover, & h3:hover, & h4:hover': {
                textDecoration: 'underline',
              },
            },
          },
        },
        MuiTableSortLabel: {
          styleOverrides: {
            root: {
              color: palette.text.secondary,
              lineHeight: 1.25,
            },
            icon: {
              marginLeft: 8,
              marginRight: 0,
            },
          },
        },
        MuiTableRow: {
          styleOverrides: {
            root: {
              '&:last-child > *': {
                borderBottom: 'none',
              },
            },
          },
        },
        MuiTableCell: {
          styleOverrides: {
            root: {
              borderBottom: `1px solid ${palette.divider}`,
            },
            head: {
              ...typography.h4,
            },
            sizeSmall: {
              '&:last-child': {
                paddingRight: 24, // Undo weird default where this isn't the same
              },
            },
          },
        },
        MuiLinearProgress: {
          styleOverrides: {
            root: {
              height: 4,
              borderRadius: 2,
              transitionTimingFunction: 'ease-in-out',
            },
          },
        },
        MuiFormGroup: {
          styleOverrides: {
            root: {
              gap: 12,
            },
            row: {
              'legend + &': {
                marginTop: 4,
              },
            },
          },
        },
        MuiRadio: {
          defaultProps: {
            color: 'default',
            icon: <></>,
            checkedIcon: <></>,
          },
          styleOverrides: {
            root: {
              backgroundColor: palette.white,
              boxShadow: paletteUtils.boxShadowButton.secondary.default,
              width: 16,
              height: 16,
              borderRadius: 16,
              marginRight: 12,
              transition: paletteUtils.transition,
              transitionProperty: 'background-color',
              position: 'relative',
              // Selection circle
              '&.Mui-checked::before': {
                content: '""',
                display: 'inline-block',
                position: 'absolute',
                backgroundColor: palette.white,
                borderRadius: 4,
                width: 6,
                height: 6,
              },
              '&.Mui-checked': {
                backgroundColor: palette.primary.main,
                boxShadow: paletteUtils.boxShadowButton.primary.default,
              },
              '&.Mui-disabled': {
                backgroundColor: palette.grey10,
                boxShadow: paletteUtils.boxShadowField.base,
              },
              '&:has(> input:focus)': {
                boxShadow: paletteUtils.boxShadowFocus,
              },
            },
          },
        },
        MuiCheckbox: {
          defaultProps: {
            color: 'default',
            disableRipple: true,
            icon: <></>,
            indeterminateIcon: (
              <span
                style={{
                  position: 'absolute',
                  width: 10,
                  height: 2,
                  borderRadius: 1,
                  backgroundColor: 'currentColor',
                }}
              />
            ),
            checkedIcon: (
              <CheckmarkIcon
                fill="currentColor"
                style={{ position: 'absolute', marginTop: 1, marginLeft: 1 }}
              />
            ),
          },
          styleOverrides: {
            root: {
              backgroundColor: palette.white,
              color: palette.secondary.main,
              boxShadow: paletteUtils.boxShadowButton.secondary.default,
              width: 16,
              height: 16,
              borderRadius: 4,
              marginRight: 12,
              transition: 'none',
              '&.Mui-checked': {
                backgroundColor: palette.primary.main,
                color: palette.white,
                boxShadow: paletteUtils.boxShadowButton.primary.default,
              },
              '&.MuiCheckbox-indeterminate': {
                backgroundColor: palette.primary.main,
                color: palette.white,
                boxShadow: paletteUtils.boxShadowButton.primary.default,
              },
              '&.Mui-focusVisible': {
                boxShadow: paletteUtils.boxShadowFocus,
              },
              '&.Mui-disabled': {
                backgroundColor: palette.grey10,
                color: palette.text.disabled,
                boxShadow: paletteUtils.boxShadowField.base,
              },
            },
          },
        },
        MuiDataGrid: {
          styleOverrides: {
            root: {
              background: palette.white,
              color: palette.slate,
              borderBottom: 1,
              borderBottomStyle: 'solid',
              borderColor: palette.divider,
            },
            columnHeaders: {
              backgroundColor: palette.grey10,
              borderBottom: 1,
              borderBottomStyle: 'solid',
              borderColor: palette.divider,
            },
            columnHeaderTitle: {
              whiteSpace: 'pre-wrap',
              lineHeight: '16px',
            },
            columnHeaderTitleContainer: {
              '& .MuiDataGrid-iconButtonContainer': {
                '& .MuiBadge-badge': { display: 'none' },
              },
            },
            pinnedColumnHeaders: {
              backgroundColor: palette.grey10,
              borderBottomStyle: 'none', // there's double border if we don't clear this
              borderColor: palette.divider,
              boxShadow: paletteUtils.boxShadow,
            },
            columnSeparator: {
              transform: 'scaleY(5)',
              transformOrigin: 'center',
              // This value is set by the library, so we need !important to control it
              opacity: '0 !important',
              '&:hover, &:focus': {
                opacity: '1 !important',
              },
            },
            iconSeparator: {
              fill: palette.grey20,
            },

            pinnedColumns: {
              boxShadow: paletteUtils.boxShadow,
              backgroundColor: palette.white,
              '&& .MuiDataGrid-row': {
                paddingLeft: '0',
                paddingRight: '0',
              },
            },
            menuIconButton: {
              marginRight: '10px',
            },
            row: {
              borderBottom: 1,
              borderBottomStyle: 'solid',
              borderColor: palette.divider,
              '&.Mui-hovered, &:hover': {
                backgroundColor: palette.cobalt05,
              },
            },
            // Note: It is intentional that the cell and the row have bottom border.
            // This is because of overflow hidden on the cell that hides the row border,
            // but the row extends beyond the cell, so this is necessary.
            cell: {
              borderBottom: 1,
              borderBottomStyle: 'solid',
              borderColor: palette.divider,
              position: 'relative',
              '& .MuiTypography-root': {
                textWrap: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              },
            },
            checkboxInput: {
              margin: 0,
            },
            footerContainer: {
              borderTop: 1,
              borderTopStyle: 'solid',
              borderColor: palette.divider,
            },
            footerCell: {
              color: palette.slate,
            },
            // @ts-expect-error this is from premium DataGrid
            toolbarQuickFilter: {
              // For some reason MUI offsets inputs by 4px by default :sob:
              '& input': {
                marginLeft: 0,
              },
            },
          },
        },
        MuiStack: {
          defaultProps: {
            useFlexGap: true,
          },
        },
        MuiTimelineDot: {
          styleOverrides: {
            root: {
              boxShadow: 'none',
            },
          },
        },
        MuiTimelineConnector: {
          styleOverrides: {
            root: {
              backgroundColor: palette.divider,
            },
          },
        },
      },
      typography: {
        ...typography,
        fontFamily: SANS_SERIF_FONT_FAMILY,
        fontFamilyMonospace: CODE_FONT_FAMILY,
        fontSize: 14,
        htmlFontSize: 14,
        fontWeightMedium: 600,
        fontWeightBold: 600,
      },
      palette,
      shape: {
        borderRadius: 6,
      },
      spacing: SPACING_PX,
      breakpoints: {
        values: {
          xs: 0,
          sm: 600,
          md: 900,
          lg: 1200,
          xl: 1536,
          addProjectDialog: 480,
          exploreProjectGridTwoColumns: 1048,
          exploreProjectGridThreeColumns: 1448,
          exploreProjectGridFourColumns: 1832,
        },
      },
      transitions: {
        duration: {
          shortest: 100,
          shorter: 125,
          short: 175,
          standard: 250,
          complex: 375,
          enteringScreen: 225,
          leavingScreen: 195,
        },
      },
    },
    ...muiLocaleConfigs
  );
}

const theme = getTheme('en-US');

export default theme;
